import React, { useEffect, useState } from "react";
import { StyledEngineProvider, ThemeProvider } from "@mui/material/styles";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import theme from "./theme/defaultTheme";
import { CssBaseline } from "@mui/material";
import { SnackbarProvider } from "notistack";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "dayjs/locale/de";
import ScrollToTop from "./components/ScrollToTop/ScrollToTop";
import HomePage from "./components/_Pages/HomePage/HomePage";
import RequireAuth from "./components/RequireAuth/RequireAuth";
import BaseRoutes from "./routes/BaseRoutes";
import PublicRoutes from "./routes/PublicRoutes";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import TechStatusProvider from "./provider/TechStatusProvider";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { DutyNotificationSnackbar } from "./components/Snackbar/DutyNotificationSnackbar";
import { CaseRefreshNotificationSnackbar } from "./components/Snackbar/CaseRefreshNotificationSnackbar";
import { CustomSnackbar } from "./components/Snackbar/CustomSnackBar";
import { SystemNoteNotificationSnackbar } from "./components/Snackbar/SystemNoteNotificationSnackbar";
import { NewMessageNotificationSnackbar } from "./components/Snackbar/NewMessageNotificationSnackbar";
import ScrollTopButton from "./components/Button/ScrollTopButton";
import { ScrollToHash } from "./components/ScrollToHash/ScrollToHash";
import ClientVersionChecker from "./components/ClientVersionChecker/ClientVersionChecker";
import { GoogleMapsApiProvider } from "./provider/GoogleMapsApiProvider";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import customParseFormat from "dayjs/plugin/customParseFormat";
import localizedFormat from "dayjs/plugin/localizedFormat";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { initWasm } from "@css-inline/css-inline-wasm";
import wasmUrl from "@css-inline/css-inline-wasm/index_bg.wasm?url";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(customParseFormat);
dayjs.extend(localizedFormat);
dayjs.extend(advancedFormat);
dayjs.locale("de");

dayjs.prototype.toJSON = function () {
  return this.format();
};

function App() {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false, // default: true
        staleTime: 1000,
      },
    },
  });

  const [wasmReady, setWasmReady] = useState(false);

  useEffect(() => {
    initWasm(wasmUrl).then(() => setWasmReady(true));
  }, []);

  if (!wasmReady) {
    return null;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <BrowserRouter>
            <GoogleMapsApiProvider>
              <ClientVersionChecker>
                <ScrollToHash />
                <SnackbarProvider
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                  }}
                  maxSnack={1}
                  Components={{
                    custom: CustomSnackbar,
                    error: CustomSnackbar,
                    dutyNotification: DutyNotificationSnackbar,
                    caseRefreshNotification: CaseRefreshNotificationSnackbar,
                    systemNoteNotification: SystemNoteNotificationSnackbar,
                    newMessageNotification: NewMessageNotificationSnackbar,
                  }}
                >
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale={"de"}
                    localeText={{
                      // localeText used For DateFields placeholder otherwise dayjs is DD/MM/YYYY instead of tt.mm.jjjj
                      fieldYearPlaceholder: (params) => "j".repeat(params.digitAmount),
                      fieldMonthPlaceholder: (params) => (params.contentType === "letter" ? "mmmm" : "mm"),
                      fieldDayPlaceholder: () => "tt",
                    }}
                  >
                    <TechStatusProvider>
                      <ScrollToTop>
                        <Routes>
                          <Route
                            path={"/services/*"}
                            element={
                              <RequireAuth>
                                <BaseRoutes />
                              </RequireAuth>
                            }
                          />
                          <Route path={"/*"} element={<PublicRoutes />} />
                          <Route
                            path={"/"}
                            element={
                              <RequireAuth>
                                <HomePage />
                              </RequireAuth>
                            }
                          />
                        </Routes>
                      </ScrollToTop>
                      <ScrollTopButton />
                    </TechStatusProvider>
                  </LocalizationProvider>
                </SnackbarProvider>
              </ClientVersionChecker>
            </GoogleMapsApiProvider>
          </BrowserRouter>
        </ThemeProvider>
      </StyledEngineProvider>
      <ReactQueryDevtools initialIsOpen={false} buttonPosition={"bottom-left"} />
    </QueryClientProvider>
  );
}

export default App;
